import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Check, Edit2, X } from 'react-feather';
import { cohortCompensationApi } from '../../../../services/api/cohortCompensation';
import type {
  CoachCompensationResult,
  CompensationLineItem,
} from '../../../../types';
import { formatCents, formatCentsCompact } from '../../../../utils/formatters';
import {
  CM_PAYMENT_TRACK_LABELS,
  COMPENSATION_STATUS_CONFIG,
  getCompensationDisplayStatus,
} from '../../../../config/compensationConfig';
import {
  groupBreakdownBySessionType,
  sumOneOnOneSubtotalCents,
} from '../../../../utils/compensationBreakdown';

interface CompensationTabContentProps {
  cohortId: string;
  coachUserId: string;
  onUpdated?: () => void;
}

/**
 * Compensation tab content rendered inside CoachDetailModal (E13-4 AC16, AC17).
 *
 * Matches mock layout: Estimated Total + Monthly Guaranteed summary cards,
 * followed by a flat breakdown list with adjust/confirm workflow.
 */
export function CompensationTabContent({
  cohortId,
  coachUserId,
  onUpdated,
}: CompensationTabContentProps) {
  const [result, setResult] = useState<CoachCompensationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adjustments, setAdjustments] = useState<Map<string, number>>(new Map());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cohortCompensationApi.getCoachCalculation(cohortId, coachUserId);
      setResult(data);
    } catch {
      // Don't show error toast — compensation may not be calculated yet (MED-659)
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [cohortId, coachUserId]);

  useEffect(() => {
    void fetchData();
    setEditing(false);
    setAdjustments(new Map());
  }, [fetchData]);

  const getEffectiveTotal = (li: CompensationLineItem): number => {
    if (adjustments.has(li.id)) return adjustments.get(li.id)!;
    return li.adjusted_total_cents ?? li.total_cents;
  };

  const handleAdjust = (lineItemId: string, value: number) => {
    setAdjustments(prev => {
      const next = new Map(prev);
      next.set(lineItemId, value);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!result) return;
    setSaving(true);
    try {
      if (adjustments.size > 0) {
        const adjustmentArray = Array.from(adjustments.entries()).map(
          ([line_item_id, adjusted_total_cents]) => ({
            line_item_id,
            adjusted_total_cents,
          }),
        );
        await cohortCompensationApi.adjustAndConfirmCoachResult(
          cohortId,
          coachUserId,
          adjustmentArray,
        );
      } else {
        await cohortCompensationApi.confirmCoachResult(cohortId, coachUserId);
      }
      notifications.show({
        title: 'Compensation Confirmed',
        message: `${result.coach_name ?? 'Coach'}'s compensation has been confirmed`,
        color: 'green',
      });
      setEditing(false);
      setAdjustments(new Map());
      onUpdated?.();
      await fetchData();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to confirm compensation',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setAdjustments(new Map());
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (!result) {
    return (
      <Text c="dimmed" ta="center" py="xl" size="sm">
        Compensation details are being prepared. Ensure the compensation configuration is confirmed and this coach is assigned to groups.
      </Text>
    );
  }

  const hasExistingAdjustments = result.line_items.some(li => li.adjusted_total_cents !== null);
  const hasPendingAdjustments = adjustments.size > 0;
  const displayStatus = getCompensationDisplayStatus(
    result.status,
    hasExistingAdjustments || hasPendingAdjustments,
  );
  const statusConfig =
    COMPENSATION_STATUS_CONFIG[displayStatus] ?? COMPENSATION_STATUS_CONFIG.pending;

  const estimatedTotal = result.estimated_total_cents ?? 0;
  const monthlyFlatRate = result.flat_rate_monthly_cents ?? 0;
  const programMonths = result.program_months ?? 0;
  const monthlyTotal = monthlyFlatRate * programMonths;

  return (
    <Stack gap="lg">
      {/* Summary cards — Estimated Total + Monthly (Guaranteed) */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Paper
          p="lg"
          radius="md"
          style={{ border: '1px solid #E8E6E1', background: '#FAFAF8' }}
        >
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.3 }}>
            Estimated Total
          </Text>
          <Text fw={700} size="32px" mt={8}>
            {formatCentsCompact(estimatedTotal)}
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            {result.group_count} {result.group_count === 1 ? 'group' : 'groups'} ·{' '}
            {result.member_count} participants
          </Text>
        </Paper>
        <Paper
          p="lg"
          radius="md"
          style={{
            border: '2px solid var(--mantine-color-medleyGreen-6)',
            background: 'white',
          }}
        >
          <Text
            size="xs"
            c="medleyGreen"
            tt="uppercase"
            fw={700}
            style={{ letterSpacing: 0.3 }}
          >
            Monthly (Guaranteed)
          </Text>
          <Text fw={700} size="32px" mt={8}>
            {monthlyFlatRate > 0 ? `${formatCentsCompact(monthlyFlatRate)}/mo` : '—'}
          </Text>
          {monthlyFlatRate > 0 && programMonths > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              {programMonths} months · {formatCentsCompact(monthlyTotal)} total
            </Text>
          )}
        </Paper>
      </SimpleGrid>

      {/* Breakdown header with status + actions */}
      <Group justify="space-between" align="center">
        <Group gap="sm">
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: 0.3 }}>
            Breakdown
          </Text>
          <Badge
            size="sm"
            color={statusConfig.color}
            variant={statusConfig.variant ?? 'light'}
          >
            {statusConfig.label}
          </Badge>
        </Group>
        {editing ? (
          <Group gap="xs">
            <Button
              variant="subtle"
              color="gray"
              size="sm"
              leftSection={<X size={14} />}
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleConfirm} loading={saving}>
              Save Adjustments
            </Button>
          </Group>
        ) : (
          result.status !== 'pending' && (
            <Group gap="xs">
              <Button
                variant="subtle"
                size="sm"
                leftSection={<Edit2 size={14} />}
                onClick={() => setEditing(true)}
              >
                Adjust
              </Button>
              {result.status !== 'confirmed' && (
                <Button
                  size="sm"
                  leftSection={<Check size={14} />}
                  onClick={handleConfirm}
                  loading={saving}
                >
                  Confirm
                </Button>
              )}
            </Group>
          )
        )}
      </Group>

      {/* Line items — MED-695: per-session-type 1:1 rows grouped under a
          "1:1 Coaching (Potential)" subsection with the Coach Debrief flat
          rate nested under the 360 Debrief row. */}
      {(() => {
        const renderLineItem = (
          li: CompensationLineItem,
          opts: { indent?: boolean; nestedLabel?: string; noTrackBadge?: boolean } = {},
        ) => {
          const hasAdjustment =
            li.adjusted_total_cents !== null || adjustments.has(li.id);
          const effectiveTotal = getEffectiveTotal(li);
          const trackLabel = CM_PAYMENT_TRACK_LABELS[li.payment_track];
          const formula =
            li.quantity !== null && li.unit_rate_cents !== null
              ? `${formatCents(li.unit_rate_cents)} × ${li.quantity}`
              : null;
          const metadataDescription =
            typeof li.metadata?.description === 'string'
              ? (li.metadata.description as string)
              : null;
          const labelText = opts.nestedLabel ?? li.label;

          return (
            <Box
              key={li.id}
              p="md"
              pl={opts.indent ? 40 : undefined}
              style={{
                borderBottom: '1px solid #F0EEE9',
                background: hasAdjustment ? '#FFFBF2' : undefined,
              }}
            >
              <Group justify="space-between" align="center" wrap="nowrap">
                <div style={{ flex: 1 }}>
                  <Group gap="xs" align="center">
                    <Text size="sm" fw={opts.nestedLabel ? 500 : 600} c={opts.nestedLabel ? 'dimmed' : undefined}>
                      {labelText}
                    </Text>
                    {li.is_potential && !opts.nestedLabel && (
                      <Badge size="xs" color="warningAmber" variant="light">
                        Potential
                      </Badge>
                    )}
                    {li.is_rate_overridden && (
                      <Badge size="xs" color="blue" variant="light">
                        Override
                      </Badge>
                    )}
                    {hasAdjustment && (
                      <Badge size="xs" color="warningAmber" variant="filled">
                        ADJUSTED
                      </Badge>
                    )}
                  </Group>
                  {(formula || metadataDescription) && !opts.nestedLabel && (
                    <Text size="xs" c="dimmed" mt={2}>
                      {metadataDescription ?? formula}
                    </Text>
                  )}
                </div>
                <Group gap="sm" align="center" wrap="nowrap">
                  {editing ? (
                    <>
                      <Text size="xs" c="dimmed" td="line-through">
                        {formatCents(li.total_cents)}
                      </Text>
                      <NumberInput
                        size="xs"
                        w={120}
                        min={0}
                        step={1}
                        decimalScale={0}
                        value={Math.round(effectiveTotal / 100)}
                        onChange={val => {
                          // Mantine v8: NumberInput.onChange returns
                          // string | number. The old typeof==='number'
                          // guard silently dropped string values so
                          // adjustments never saved (MED-910 QA).
                          const num =
                            typeof val === 'number' ? val : parseFloat(String(val));
                          if (Number.isFinite(num)) {
                            handleAdjust(li.id, Math.round(num * 100));
                          }
                        }}
                        styles={{ input: { textAlign: 'right', fontWeight: 600 } }}
                      />
                    </>
                  ) : (
                    <Group gap={4} align="center">
                      {hasAdjustment && effectiveTotal !== li.total_cents && (
                        <Text size="sm" td="line-through" c="dimmed">
                          {formatCents(li.total_cents)}
                        </Text>
                      )}
                      <Text size="sm" fw={700}>
                        {formatCents(effectiveTotal)}
                      </Text>
                    </Group>
                  )}
                  {trackLabel && !opts.noTrackBadge && (
                    <Badge
                      size="sm"
                      color={trackLabel.color}
                      variant={trackLabel.variant}
                      style={{ minWidth: 70, textAlign: 'center' }}
                    >
                      {trackLabel.label}
                    </Badge>
                  )}
                </Group>
              </Group>
            </Box>
          );
        };

        const grouped = groupBreakdownBySessionType(result.line_items);
        const oneOnOneSubtotal = sumOneOnOneSubtotalCents(
          grouped.oneOnOneGroups,
          getEffectiveTotal,
        );

        return (
          <Paper
            radius="md"
            style={{ border: '1px solid #E8E6E1', background: 'white', overflow: 'hidden' }}
          >
            {grouped.hasOneOnOneSection && (
              <>
                <Box p="md" style={{ background: '#FAFAF8', borderBottom: '1px solid #F0EEE9' }}>
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Group gap="xs" align="center">
                      <Text size="sm" fw={700}>
                        1:1 Coaching
                      </Text>
                      <Badge size="xs" color="warningAmber" variant="light">
                        Potential
                      </Badge>
                    </Group>
                    <Text size="sm" fw={700}>
                      {formatCents(oneOnOneSubtotal)}
                    </Text>
                  </Group>
                </Box>
                {grouped.oneOnOneGroups.map(g => (
                  <Fragment key={g.lineItem.id}>
                    {renderLineItem(g.lineItem, { indent: true, noTrackBadge: true })}
                    {g.nestedCoachDebrief &&
                      renderLineItem(g.nestedCoachDebrief, {
                        indent: true,
                        nestedLabel: '+ Coach Debrief Flat Rate',
                        noTrackBadge: true,
                      })}
                  </Fragment>
                ))}
              </>
            )}
            {grouped.otherLineItems.map(li => renderLineItem(li))}
          </Paper>
        );
      })()}

    </Stack>
  );
}
